import { useRouter } from 'next/router'
import { useCallback, useEffect, useState } from 'react'

import {
  HouseOwnerLightContextFieldFragment,
  LoginMutationVariables,
  useGetMeOwnerMutation,
  useLoginMutation,
  useLogoutMutation,
} from '@/lib/graphql'

import RegenerateToken from '@/components/layout/regenerate-token/RegenerateToken'

import { queryClient } from '@/api/clients'
import { formatGqlError } from '@/utils/format-error'

import { AuthContext, AuthContextInterface } from './auth.context'

export default function AuthProvider({
  children,
  isAuthenticateTokenExpired,
}: {
  children: React.ReactNode
  isAuthenticateTokenExpired?: boolean
}) {
  const [user, setUser] = useState<HouseOwnerLightContextFieldFragment | null>(
    null,
  )
  const [isLoadingAuthentication, setIsLoadingAuthentication] =
    useState<boolean>(true)
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false)

  const { mutateAsync: loginMutation } = useLoginMutation()
  const { mutateAsync: logoutMutation } = useLogoutMutation()
  const { mutateAsync: getMeOwnerMutation } = useGetMeOwnerMutation()

  const router = useRouter()

  const dispatchLogin = (user: HouseOwnerLightContextFieldFragment) => {
    setIsAuthenticated(true)
    setUser(user)
    setIsLoadingAuthentication(false)
  }

  const runLogin = useCallback(
    async (params: LoginMutationVariables) => {
      const response = await loginMutation(params)
      dispatchLogin(response.loginOwner)
    },
    [loginMutation],
  )

  const runLogout = useCallback(async () => {
    try {
      const response = await logoutMutation({})

      if (response.logoutHouseOwner.success) {
        setUser(null)
        setIsAuthenticated(false)
        queryClient.removeQueries()
        router.push('/')
      }
    } catch (error) {
      console.error(formatGqlError(error))
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logoutMutation])

  const fetchUserOnMount = useCallback(async () => {
    const authenticateToken = router.query?.['authenticate-token'] || null
    const response = await getMeOwnerMutation({})
    const owner = response?.owner

    if (owner) {
      dispatchLogin(owner)
    } else {
      setIsAuthenticated(false)
      setUser(null)
      setIsLoadingAuthentication(false)
      if (router.pathname !== '/' && !authenticateToken) {
        router.push('/')
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getMeOwnerMutation])

  useEffect(() => {
    if (process.env.NEXT_PUBLIC_MAINTENANCE_MODE !== 'true') {
      fetchUserOnMount()
    }
  }, [fetchUserOnMount])

  useEffect(() => {
    if (router.pathname === '/' && router.query.logout === 'true') {
      setUser(null)
      setIsAuthenticated(false)
      // eslint-disable-next-line unused-imports/no-unused-vars
      const { logout, ...newQuery } = router.query
      const newPathname = {
        pathname: router.pathname,
        ...(newQuery && { query: newQuery }),
      }
      router.replace(newPathname, undefined, { shallow: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.query])

  const state: AuthContextInterface = {
    user,
    isAuthenticated,
    isLoadingAuthentication,
    runLogin,
    runLogout,
    dispatchLogin,
  }

  return (
    <AuthContext.Provider value={state}>
      {isAuthenticateTokenExpired ? <RegenerateToken /> : children}
    </AuthContext.Provider>
  )
}
